import React, { useState, useEffect, useContext, useCallback } from 'react';
import RC_Style from '../../../../../assets/css/components/admin/plugins/reportesComercialesStyle';
import SelectEnterprise from '../components/SelectEnterprise';
import SelectRate from '../components/SelectRate';
import SelectReport from '../components/SelectReport';
import useRcReport from '../functions/useRcReport';
import { BiHide, BiShow } from "react-icons/bi";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Loader from '../components/Loader';
import { GlobalContext } from '../../../../../contexts/GlobalContext';

const WIZARD_STEPS = [
    { key: 'credentials', label: 'Credenciales', num: 1 },
    { key: 'select-company', label: 'Empresa', num: 2 },
    { key: 'configure', label: 'Configurar', num: 3 },
];

function StepIndicator({ currentStep }) {
    const currentIdx = WIZARD_STEPS.findIndex(s => s.key === currentStep);
    return (
        <RC_Style.StepBar>
            {WIZARD_STEPS.map((step, i) => {
                const isActive = step.key === currentStep;
                const isCompleted = i < currentIdx;
                return (
                    <React.Fragment key={step.key}>
                        {i > 0 && <RC_Style.StepConnector $completed={isCompleted} />}
                        <RC_Style.StepItem>
                            <RC_Style.StepNumber $active={isActive} $completed={isCompleted}>
                                {isCompleted ? '✓' : step.num}
                            </RC_Style.StepNumber>
                            <RC_Style.StepLabel $active={isActive} $completed={isCompleted}>
                                {step.label}
                            </RC_Style.StepLabel>
                        </RC_Style.StepItem>
                    </React.Fragment>
                );
            })}
        </RC_Style.StepBar>
    );
}

function FilterModeToggle({ mode, setMode }) {
    return (
        <RC_Style.FilterToggleGroup>
            <RC_Style.FilterToggleBtn type="button" $active={mode === 'exclude'} onClick={() => setMode('exclude')}>
                Excluir seleccionados
            </RC_Style.FilterToggleBtn>
            <RC_Style.FilterToggleBtn type="button" $active={mode === 'include'} onClick={() => setMode('include')}>
                Solo incluir seleccionados
            </RC_Style.FilterToggleBtn>
        </RC_Style.FilterToggleGroup>
    );
}

export default function ReportesComerciales() {
    const {
        getInventoryReport,
        searchOdooBrands,
        searchOdooCategories,
        connectToOdoo,
        getOdooPricelists,
        generatePricelistReport,
        handleAxiosError,
    } = useRcReport();
    const globalContext = useContext(GlobalContext);

    // ─── UI state ────────────────────────────────────────────────────────────
    const [priceReportStep, setPriceReportStep] = useState('credentials');
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Cargando...');

    // ─── Credentials ─────────────────────────────────────────────────────────
    const [usuario, setUsuario] = useState('');
    const [clave, setClave] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [reporteSeleccionado, setReporteSeleccionado] = useState('reporte-precios');

    // ─── Legacy: inventory / consignment reports ─────────────────────────────
    const [login, setLogin] = useState(false);
    const [userIsReadOnly, setUserIsReadOnly] = useState(false);
    const [passwordIsReadOnly, setPasswordIsReadOnly] = useState(false);
    const [reportIsReadOnly, setReportIsReadOnly] = useState(false);
    const [enterprise, setEnterprise] = useState('');
    const [enterprisesData, setEnterprisesData] = useState([]);
    const [errorLoadingEnterprisesData, setErrorLoadingEnterprisesData] = useState(false);
    const [ownersData, setOwnersData] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [showOwnersStep, setShowOwnersStep] = useState(false);

    // ─── Price report ────────────────────────────────────────────────────────
    const [odooCompanies, setOdooCompanies] = useState([]);
    const [selectedOdooCompany, setSelectedOdooCompany] = useState(null);
    const [wholesaleRate, setWholesaleRate] = useState();
    const [ratesData, setRatesData] = useState([]);
    const [pvpRate, setPvpRate] = useState(null);
    const [loadingRatesData, setLoadingRatesData] = useState(false);
    const [errorLoadingRatesData, setErrorLoadingRatesData] = useState(false);

    // ─── Filters ─────────────────────────────────────────────────────────────
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brandFilterMode, setBrandFilterMode] = useState('exclude');
    const [categoryFilterMode, setCategoryFilterMode] = useState('exclude');

    const [defaultBrandOptions, setDefaultBrandOptions] = useState([]);
    const [defaultCategoryOptions, setDefaultCategoryOptions] = useState([]);
    const [onlyWithStock, setOnlyWithStock] = useState(false);
    const [blockId, setBlockId] = useState('');
    const [loadingOdooData, setLoadingOdooData] = useState(false);

    const [blockedInventoryIds, setBlockedInventoryIds] = useState([]);

    /** Tarifas y opciones iniciales de filtros cuando el usuario elige empresa Odoo */
    useEffect(() => {
        if (!selectedOdooCompany || !usuario || !clave) {
            setRatesData([]);
            setDefaultBrandOptions([]);
            setDefaultCategoryOptions([]);
            setLoadingRatesData(false);
            setLoadingOdooData(false);
            setErrorLoadingRatesData(false);
            setWholesaleRate(undefined);
            setPvpRate(null);
            setBrands([]);
            setCategories([]);
            return;
        }

        let cancelled = false;

        const loadCompanyData = async () => {
            setLoadingRatesData(true);
            setLoadingOdooData(true);
            setErrorLoadingRatesData(false);

            try {
                const rates = await getOdooPricelists(usuario, clave, selectedOdooCompany.value);
                if (cancelled) return;
                setRatesData(rates || []);
                setWholesaleRate(undefined);
                setPvpRate(null);
                setBrands([]);
                setCategories([]);

                const [brandsResult, categoriesResult] = await Promise.allSettled([
                    searchOdooBrands(usuario, clave, selectedOdooCompany.value, ''),
                    searchOdooCategories(usuario, clave, selectedOdooCompany.value, ''),
                ]);
                if (cancelled) return;

                if (brandsResult.status === 'fulfilled') {
                    setDefaultBrandOptions(brandsResult.value.map(b => ({ value: b, label: b })));
                } else {
                    setDefaultBrandOptions([]);
                }
                if (categoriesResult.status === 'fulfilled') {
                    setDefaultCategoryOptions(
                        categoriesResult.value.map(c => ({ value: c.id, label: c.display_name || c.name })),
                    );
                } else {
                    setDefaultCategoryOptions([]);
                }
            } catch (error) {
                if (!cancelled) {
                    setErrorLoadingRatesData(true);
                    setRatesData([]);
                    setDefaultBrandOptions([]);
                    setDefaultCategoryOptions([]);
                    setWholesaleRate(undefined);
                    setPvpRate(null);
                    setBrands([]);
                    setCategories([]);
                    globalContext.showModalWithMsg(handleAxiosError(error));
                }
            } finally {
                if (!cancelled) {
                    setLoadingRatesData(false);
                    setLoadingOdooData(false);
                }
            }
        };

        loadCompanyData();
        return () => {
            cancelled = true;
        };
    }, [selectedOdooCompany, usuario, clave]);

    useEffect(() => {
        if (reporteSeleccionado !== 'reporte-productos-consignacion') {
            setShowOwnersStep(false);
            setSelectedOwner(null);
            setOwnersData([]);
        }
    }, [reporteSeleccionado]);

    useEffect(() => {
        if (reporteSeleccionado === 'reporte-productos-consignacion' && enterprise && showOwnersStep) {
            setShowOwnersStep(false);
            setSelectedOwner(null);
            setOwnersData([]);
        }
    }, [enterprise, reporteSeleccionado]);

    // ─── Async search callbacks ──────────────────────────────────────────────

    const loadBrandOptions = useCallback(async (inputValue) => {
        if (!selectedOdooCompany || !usuario || !clave) return [];
        try {
            const results = await searchOdooBrands(usuario, clave, selectedOdooCompany.value, inputValue);
            return results.map(b => ({ value: b, label: b }));
        } catch {
            return [];
        }
    }, [selectedOdooCompany, usuario, clave]);

    const loadCategoryOptions = useCallback(async (inputValue) => {
        if (!selectedOdooCompany || !usuario || !clave) return [];
        try {
            const results = await searchOdooCategories(usuario, clave, selectedOdooCompany.value, inputValue);
            return results.map(c => ({ value: c.id, label: c.display_name || c.name }));
        } catch {
            return [];
        }
    }, [selectedOdooCompany, usuario, clave]);

    // ─── Helpers ─────────────────────────────────────────────────────────────


    // ─── Price report handlers ───────────────────────────────────────────────

    const handleConnectToOdoo = async (e) => {
        e.preventDefault();
        if (!usuario || !clave) return;
        setLoading(true);
        setLoadingText('Conectando a Odoo...');
        try {
            const companies = await connectToOdoo(usuario, clave);
            if (companies?.length > 0) {
                setOdooCompanies(companies);
                setPriceReportStep('select-company');
            } else {
                globalContext.showModalWithMsg('No se encontraron empresas disponibles en Odoo');
            }
        } catch (error) {
            globalContext.showModalWithMsg(handleAxiosError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePriceReport = async (e) => {
        e.preventDefault();
        if (!selectedOdooCompany || !wholesaleRate) return;
        setLoading(true);
        setLoadingText('Iniciando generación del reporte...');
        try {
            const wholesaleRateForReport = ratesData.find(rt => rt.id === wholesaleRate.value);
            if (!wholesaleRateForReport) {
                globalContext.showModalWithMsg('La tarifa seleccionada ya no está disponible. Seleccione otra.');
                setLoading(false);
                return;
            }
            const brandValues = brands.map(b => b.value);
            const categoryValues = categories.map(c => c.value);
            await generatePricelistReport({
                odooUser: usuario,
                odooPassword: clave,
                odooEnterpriceInfo: { id: selectedOdooCompany.value, name: selectedOdooCompany.label },
                odooWholesaleRateInfo: wholesaleRateForReport,
                odooRetailRateId: pvpRate?.value,
                banInventories: blockedInventoryIds,
                brandFilters: brandValues.length > 0 ? { mode: brandFilterMode, values: brandValues } : undefined,
                categoryFilters: categoryValues.length > 0 ? { mode: categoryFilterMode, values: categoryValues } : undefined,
                onlyWithStock,
            });
            globalContext.showModalWithMsg('El reporte se está generando. Consulte el progreso en la vista de segundo plano.');
        } catch (error) {
            globalContext.showModalWithMsg(handleAxiosError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleRestartPriceReport = () => {
        setPriceReportStep('credentials');
        setOdooCompanies([]);
        setSelectedOdooCompany(null);
        setRatesData([]);
        setWholesaleRate(undefined);
        setPvpRate(null);
        setBrands([]);
        setCategories([]);
        setDefaultBrandOptions([]);
        setDefaultCategoryOptions([]);
        setOnlyWithStock(false);
        setBlockedInventoryIds([]);
        setBlockId('');
    };

    const addIdToBlock = (e) => {
        e.preventDefault();
        const trimmed = blockId.trim();
        if (!trimmed) return;
        const parsed = Number(trimmed);
        if (Number.isNaN(parsed)) return;
        if (blockedInventoryIds.includes(parsed)) return;
        setBlockedInventoryIds(prev => [...prev, parsed]);
        setBlockId('');
    };

    const removeIdFromBlock = (idToRemove) => {
        setBlockedInventoryIds(prev => prev.filter(id => id !== idToRemove));
    };

    // ─── Legacy report handlers ──────────────────────────────────────────────

    const handleSubmitLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoadingText('Iniciando sesión...');
        if (!login) {
            try {
                let data;
                if (reporteSeleccionado === 'reporte-inventario')
                    data = await getInventoryReport(usuario, clave, enterprise, true, 'report-sales-costs');
                else if (reporteSeleccionado === 'reporte-productos-consignacion')
                    data = await getInventoryReport(usuario, clave, enterprise, true, 'report-movements');
                if (data) {
                    setEnterprisesData(data);
                    setLogin(true);
                    setUserIsReadOnly(true);
                    setPasswordIsReadOnly(true);
                    setReportIsReadOnly(true);
                }
            } catch (error) {
                setErrorLoadingEnterprisesData(true);
                globalContext.showModalWithMsg(handleAxiosError(error));
            }
        } else {
            setLogin(false);
            setPasswordIsReadOnly(false);
            setUserIsReadOnly(false);
            setReportIsReadOnly(false);
            setEnterprise('');
            setEnterprisesData([]);
            setErrorLoadingEnterprisesData(false);
            setRatesData([]);
            setShowOwnersStep(false);
            setOwnersData([]);
            setSelectedOwner(null);
        }
        setLoading(false);
    };

    const handleNextStep = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoadingText('Obteniendo proveedores...');
        try {
            const response = await getInventoryReport(usuario, clave, enterprise, false, 'report-movements', true);
            const data = Array.isArray(response) ? response : response?.data;
            if (Array.isArray(data)) {
                setOwnersData(data);
                setShowOwnersStep(true);
            }
        } catch (error) {
            globalContext.showModalWithMsg(handleAxiosError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReportWithOwner = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoadingText('Generando reporte...');
        try {
            await getInventoryReport(usuario, clave, enterprise, false, 'report-movements', false, {
                id: selectedOwner.value,
                name: selectedOwner.label,
            });
        } catch (error) {
            globalContext.showModalWithMsg(handleAxiosError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitGetReport = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoadingText('Generando reporte...');
        try {
            if (reporteSeleccionado === 'reporte-inventario')
                await getInventoryReport(usuario, clave, enterprise, false, 'report-sales-costs');
            else if (reporteSeleccionado === 'reporte-productos-consignacion')
                await getInventoryReport(usuario, clave, enterprise, false, 'report-movements');
        } catch (error) {
            globalContext.showModalWithMsg(handleAxiosError(error));
        } finally {
            setLoading(false);
        }
    };

    // ─── Derived ─────────────────────────────────────────────────────────────

    const isPriceReport = reporteSeleccionado === 'reporte-precios';
    const credentialsLocked = isPriceReport && priceReportStep !== 'credentials';
    const canGenerate = wholesaleRate && !loadingOdooData && !loadingRatesData;

    // ─── Render ──────────────────────────────────────────────────────────────

    return (
        <RC_Style.Cont>
            <Loader show={loading} text={loadingText} />
            <RC_Style.Title>Reportes Comerciales</RC_Style.Title>

            <RC_Style.SubCont>
                {/* ─── Credentials ────────────────────────────────────────── */}
                <RC_Style.SubTitle>Credenciales de Odoo</RC_Style.SubTitle>

                <RC_Style.ContForm onSubmit={isPriceReport ? handleConnectToOdoo : handleSubmitLogin}>
                    <RC_Style.FormGroup>
                        <RC_Style.Label htmlFor="usuario">Usuario</RC_Style.Label>
                        <RC_Style.Input
                            type="text"
                            id="usuario"
                            value={usuario}
                            onChange={(e) => setUsuario(e.target.value)}
                            placeholder="usuario@empresa.com"
                            required
                            readOnly={userIsReadOnly || credentialsLocked}
                        />
                    </RC_Style.FormGroup>

                    <RC_Style.FormGroup>
                        <RC_Style.Label htmlFor="clave">Contraseña</RC_Style.Label>
                        <div style={{ position: 'relative' }}>
                            <RC_Style.Input
                                type={showPass ? "text" : "password"}
                                id="clave"
                                value={clave}
                                onChange={(e) => setClave(e.target.value)}
                                placeholder="••••••••"
                                required
                                readOnly={passwordIsReadOnly || credentialsLocked}
                            />
                            <RC_Style.BtnShowPass type="button" onClick={() => setShowPass(prev => !prev)}>
                                {showPass ? <BiHide /> : <BiShow />}
                            </RC_Style.BtnShowPass>
                        </div>
                    </RC_Style.FormGroup>

                    <RC_Style.FormGroup fullWidth>
                        <SelectReport
                            reporteSeleccionado={reporteSeleccionado}
                            setReporteSeleccionado={setReporteSeleccionado}
                            isReadOnly={reportIsReadOnly || credentialsLocked}
                        />
                    </RC_Style.FormGroup>

                    <RC_Style.ContFormButton>
                        {isPriceReport ? (
                            priceReportStep === 'credentials'
                                ? <RC_Style.Button type="submit" disabled={!usuario || !clave}>Conectar a Odoo</RC_Style.Button>
                                : <RC_Style.LinkButton type="button" onClick={handleRestartPriceReport}>← Cambiar credenciales</RC_Style.LinkButton>
                        ) : (
                            <RC_Style.Button type="submit">{!login ? 'Iniciar sesión' : 'Reiniciar'}</RC_Style.Button>
                        )}
                    </RC_Style.ContFormButton>
                </RC_Style.ContForm>

                {/* ─── Price report wizard ────────────────────────────────── */}
                {isPriceReport && priceReportStep !== 'credentials' && (
                    <>
                        <RC_Style.Divider />
                        <RC_Style.SubTitle>Reporte de precios</RC_Style.SubTitle>
                        <StepIndicator currentStep={priceReportStep} />

                        {/* Step 2: Select company */}
                        {priceReportStep === 'select-company' && (
                            <RC_Style.ContForm2 onSubmit={(e) => { e.preventDefault(); if (selectedOdooCompany) setPriceReportStep('configure'); }}>
                                <RC_Style.FormGroup>
                                    <RC_Style.Label>Empresa de Odoo</RC_Style.Label>
                                    <Select
                                        value={selectedOdooCompany}
                                        onChange={setSelectedOdooCompany}
                                        options={odooCompanies.map(c => ({ value: c.id, label: c.name }))}
                                        placeholder="Seleccione una empresa..."
                                        styles={RC_Style.customStyles}
                                        isClearable
                                        isSearchable
                                    />
                                </RC_Style.FormGroup>

                                {loadingRatesData && (
                                    <RC_Style.StatusText $variant="info">Cargando datos de la empresa...</RC_Style.StatusText>
                                )}

                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    {selectedOdooCompany && !loadingRatesData && (
                                        <RC_Style.Button type="submit">Continuar →</RC_Style.Button>
                                    )}
                                    <RC_Style.LinkButton type="button" onClick={handleRestartPriceReport}>
                                        ← Volver
                                    </RC_Style.LinkButton>
                                </div>
                            </RC_Style.ContForm2>
                        )}

                        {/* Step 3: Configure & generate */}
                        {priceReportStep === 'configure' && (
                            <RC_Style.ContForm2 onSubmit={handleGeneratePriceReport}>
                                {loadingOdooData && (
                                    <RC_Style.StatusText $variant="info">Cargando marcas y categorías desde Odoo...</RC_Style.StatusText>
                                )}
                                {errorLoadingRatesData && (
                                    <RC_Style.StatusText $variant="error">Error al cargar las tarifas. Intente nuevamente.</RC_Style.StatusText>
                                )}

                                {!errorLoadingRatesData && (
                                    <>
                                        <SelectRate setRate={setWholesaleRate} rate={wholesaleRate} label={'Tarifa al mayor'} rates={ratesData} />
                                        <SelectRate setRate={setPvpRate} rate={pvpRate} label={'Precio sugerido de venta (PVP)'} rates={ratesData} />

                                        <RC_Style.Divider />

                                        {/* Category filter */}
                                        <RC_Style.FormGroup>
                                            <RC_Style.Label>Filtro de categorías</RC_Style.Label>
                                            <FilterModeToggle mode={categoryFilterMode} setMode={setCategoryFilterMode} />
                                        </RC_Style.FormGroup>
                                        <RC_Style.FormGroup>
                                            <AsyncSelect
                                                isMulti
                                                cacheOptions
                                                defaultOptions={defaultCategoryOptions}
                                                loadOptions={loadCategoryOptions}
                                                value={categories}
                                                onChange={(selected) => setCategories(selected || [])}
                                                placeholder="Buscar categorías..."
                                                noOptionsMessage={() => "Sin resultados"}
                                                loadingMessage={() => "Buscando en Odoo..."}
                                                isDisabled={loadingOdooData}
                                                styles={RC_Style.customStyles}
                                            />
                                        </RC_Style.FormGroup>

                                        {/* Brand filter */}
                                        <RC_Style.FormGroup>
                                            <RC_Style.Label>Filtro de marcas</RC_Style.Label>
                                            <FilterModeToggle mode={brandFilterMode} setMode={setBrandFilterMode} />
                                        </RC_Style.FormGroup>
                                        <RC_Style.FormGroup>
                                            <AsyncSelect
                                                isMulti
                                                cacheOptions
                                                defaultOptions={defaultBrandOptions}
                                                loadOptions={loadBrandOptions}
                                                value={brands}
                                                onChange={(selected) => setBrands(selected || [])}
                                                placeholder="Buscar marcas..."
                                                noOptionsMessage={() => "Sin resultados"}
                                                loadingMessage={() => "Buscando en Odoo..."}
                                                isDisabled={loadingOdooData}
                                                styles={RC_Style.customStyles}
                                            />
                                        </RC_Style.FormGroup>

                                        <RC_Style.Divider />

                                        {/* Stock filter */}
                                        <RC_Style.CheckboxLabel>
                                            <input
                                                type="checkbox"
                                                checked={onlyWithStock}
                                                onChange={(e) => setOnlyWithStock(e.target.checked)}
                                            />
                                            Solo productos con stock ({selectedOdooCompany?.label})
                                        </RC_Style.CheckboxLabel>

                                        {/* Blocked inventory IDs */}
                                        <RC_Style.FormGroup>
                                            <RC_Style.Label>Inventarios bloqueados (IDs)</RC_Style.Label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <RC_Style.Input
                                                    type="text"
                                                    value={blockId}
                                                    onChange={(e) => setBlockId(e.target.value)}
                                                    placeholder="Ingrese un ID..."
                                                    onKeyDown={(e) => { if (e.key === 'Enter') addIdToBlock(e); }}
                                                />
                                                <RC_Style.OutlineButton type="button" onClick={addIdToBlock}>
                                                    Agregar
                                                </RC_Style.OutlineButton>
                                            </div>
                                            {blockedInventoryIds.length > 0 && (
                                                <RC_Style.TagContainer>
                                                    {blockedInventoryIds.map((id, i) => (
                                                        <RC_Style.Tag key={i}>
                                                            {id}
                                                            <button type="button" onClick={() => removeIdFromBlock(id)}>×</button>
                                                        </RC_Style.Tag>
                                                    ))}
                                                </RC_Style.TagContainer>
                                            )}
                                        </RC_Style.FormGroup>

                                        <RC_Style.Divider />

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <RC_Style.Button type="submit" disabled={!canGenerate}>
                                                Generar reporte
                                            </RC_Style.Button>
                                            <RC_Style.LinkButton type="button" onClick={() => setPriceReportStep('select-company')}>
                                                ← Cambiar empresa
                                            </RC_Style.LinkButton>
                                        </div>
                                    </>
                                )}
                            </RC_Style.ContForm2>
                        )}
                    </>
                )}

                {/* ─── Inventory / Consignment reports (legacy) ───────────── */}
                {login && !isPriceReport && (
                    <>
                        <RC_Style.Divider />

                        {reporteSeleccionado === 'reporte-productos-consignacion' ? (
                            <>
                                <RC_Style.SubTitle>Reporte de productos a consignación</RC_Style.SubTitle>
                                <RC_Style.ContForm onSubmit={showOwnersStep ? handleGenerateReportWithOwner : handleNextStep}>
                                    {!errorLoadingEnterprisesData && (
                                        <SelectEnterprise setEnterprise={setEnterprise} enterprise={enterprise} enterprises={enterprisesData} />
                                    )}
                                    {errorLoadingEnterprisesData && (
                                        <RC_Style.StatusText $variant="error">Error cargando las empresas</RC_Style.StatusText>
                                    )}

                                    {showOwnersStep && (
                                        <RC_Style.FormGroup>
                                            <RC_Style.Label>Proveedor</RC_Style.Label>
                                            <Select
                                                value={selectedOwner}
                                                onChange={setSelectedOwner}
                                                options={ownersData.map(o => ({ value: o.id, label: o.name }))}
                                                placeholder="Seleccione un proveedor..."
                                                styles={RC_Style.customStyles}
                                                isClearable
                                                isSearchable
                                            />
                                        </RC_Style.FormGroup>
                                    )}

                                    <RC_Style.ContFormButton>
                                        {enterprise && !showOwnersStep && (
                                            <RC_Style.Button type="submit">Siguiente →</RC_Style.Button>
                                        )}
                                        {showOwnersStep && selectedOwner && (
                                            <RC_Style.Button type="submit">Generar reporte</RC_Style.Button>
                                        )}
                                    </RC_Style.ContFormButton>
                                </RC_Style.ContForm>
                            </>
                        ) : reporteSeleccionado === 'reporte-inventario' ? (
                            <>
                                <RC_Style.SubTitle>Reporte de inventario</RC_Style.SubTitle>
                                <RC_Style.ContForm onSubmit={handleSubmitGetReport}>
                                    {!errorLoadingEnterprisesData && (
                                        <SelectEnterprise setEnterprise={setEnterprise} enterprise={enterprise} enterprises={enterprisesData} />
                                    )}
                                    {errorLoadingEnterprisesData && (
                                        <RC_Style.StatusText $variant="error">Error cargando las empresas</RC_Style.StatusText>
                                    )}
                                    <RC_Style.ContFormButton>
                                        {enterprise && <RC_Style.Button type="submit">Generar reporte</RC_Style.Button>}
                                    </RC_Style.ContFormButton>
                                </RC_Style.ContForm>
                            </>
                        ) : null}
                    </>
                )}
            </RC_Style.SubCont>
        </RC_Style.Cont>
    );
}
